import { NavLink, useLocation } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  HomeNavIcon, PatientsNavIcon, AppointmentsNavIcon, PrescriptionsNavIcon, BillingNavIcon,
  IpdNavIcon, ReportsNavIcon, CalculatorNavIcon, TemplatesNavIcon, SettingsNavIcon, AdminNavIcon,
  type NavIcon,
} from "@/components/icons/NavIcons";
import { useSidebar } from "@/context/SidebarContext";
import { useAuth } from "@/context/AuthContext";
import { usePermission } from "@/hooks/usePermission";
import { cn } from "@/lib/utils";
import { BrandMark } from "@/components/BrandMark";
import { useState, useCallback, useRef } from "react";
import React from "react";

export type NavItem = {
  label: string;
  icon: NavIcon;
  to: string;
  roles: string[];
  permission?: string;
  requireClinical?: boolean;
  exactMatch?: boolean;
};

export function isNavItemActive(pathname: string, to: string, exactMatch?: boolean) {
  if (exactMatch) return pathname === to;
  return pathname === to || pathname.startsWith(to + "/");
}

/** Navigation entries the current user may see — shared by the sidebar and the phone tab bar. */
export function useNavItems(): NavItem[] {
  const { currentUser } = useAuth();
  const { hasFeature, can } = usePermission();

  const userRole = currentUser?.role || "doctor";
  const hasClinicalProfile = !!currentUser?.doctor_id;
  // Route based on clinical profile (doctor_id), not just role
  // Staff and admins without doctor_id → front-desk routes
  // Anyone with doctor_id (doctor or doctor-admin) → clinical routes
  const dashboardRoute = userRole === "staff" ? "/frontdesk" : "/";
  const appointmentsRoute = hasClinicalProfile ? "/appointments" : "/frontdesk/appointments";
  const patientsRoute = hasClinicalProfile ? "/patients" : "/frontdesk/patients";

  const navItems: NavItem[] = [
    { label: "Dashboard",    icon: HomeNavIcon,          to: dashboardRoute, roles: ["doctor", "staff", "admin"], exactMatch: true },
    { label: "Patients",     icon: PatientsNavIcon,         to: patientsRoute,  roles: ["doctor", "staff", "admin"], permission: "patients:read", requireClinical: true },
    { label: "Appointments", icon: AppointmentsNavIcon,      to: appointmentsRoute, roles: ["doctor", "staff", "admin"], permission: "appointments:read" },
    { label: "Prescriptions",icon: PrescriptionsNavIcon,      to: "/prescriptions",roles: ["doctor", "admin"], permission: "prescriptions:read", requireClinical: true },
    ...(hasFeature("peds_calculator")
      ? [{ label: "Peds Calculator", icon: CalculatorNavIcon, to: "/peds-calculator", roles: ["doctor", "admin"], requireClinical: true }]
      : []),
    { label: "Billing",      icon: BillingNavIcon,    to: "/billing",      roles: ["doctor", "admin", "staff"], permission: "billing:read" },
    { label: "IPD",          icon: IpdNavIcon,         to: "/ipd",          roles: ["doctor", "admin", "staff"], permission: "ipd:read" },
    ...(hasFeature("reports")
      ? [{ label: "Reports", icon: ReportsNavIcon, to: "/reports", roles: ["doctor", "admin", "staff"], permission: "reports:read" }]
      : []),
    { label: "Presets",      icon: TemplatesNavIcon, to: "/presets",      roles: ["doctor", "admin"], permission: "prescriptions:read", requireClinical: true },
    { label: "Settings",     icon: SettingsNavIcon,      to: "/settings",     roles: ["admin", "doctor"], permission: "settings:read" },
  ];

  const visibleNavItems = navItems.filter((item) => {
    // Check role
    if (!item.roles.includes(userRole)) return false;
    // Check permission
    if (item.permission && !can(item.permission)) return false;
    // Check clinical profile requirement (for clinical routes)
    if (item.requireClinical && !hasClinicalProfile) return false;
    return true;
  });

  if (currentUser?.is_platform_admin) {
    visibleNavItems.push({ label: "Platform Admin", icon: AdminNavIcon, to: "/platform-admin", roles: [], requireClinical: false });
  }
  return visibleNavItems;
}

export default function Sidebar() {
  const location = useLocation();
  const { collapsed, toggle, isInitialized } = useSidebar();
  const [activeTooltip, setActiveTooltip] = useState<string | null>(null);
  const tooltipTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const visibleNavItems = useNavItems();
  const isActiveRoute = (to: string, exactMatch?: boolean) => isNavItemActive(location.pathname, to, exactMatch);

  // Per-icon label tooltip (collapsed state only) — a small floating hint, not a
  // layout-affecting overlay, so it carries none of the content-clipping risk that
  // expanding the whole sidebar on hover did.
  const handleItemMouseEnter = useCallback((label: string) => {
    if (collapsed) {
      if (tooltipTimeoutRef.current) clearTimeout(tooltipTimeoutRef.current);
      setActiveTooltip(label);
    }
  }, [collapsed]);

  const handleItemMouseLeave = useCallback(() => {
    if (tooltipTimeoutRef.current) clearTimeout(tooltipTimeoutRef.current);
    setActiveTooltip(null);
  }, []);

  const handleToggleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setActiveTooltip(null);
    toggle();
  }, [toggle]);

  return (
      <aside
          aria-label="Sidebar navigation"
          className={cn(
              "fixed left-0 top-0 z-40 h-[100dvh] flex flex-col border-r border-border overflow-hidden",
              "bg-[hsl(var(--sidebar-bg))] text-[hsl(var(--sidebar-foreground))]",
              !isInitialized && "sidebar-loading",
              collapsed ? "w-16" : "w-52"
          )}
      >
        {/* Brand — a 36px mark with 12px above and below, no divider (as in the design) */}
        <div className={cn("flex-shrink-0 flex items-center pt-3 pb-3", collapsed ? "justify-center" : "gap-2.5 px-4")}>
          <BrandMark className="w-9 h-9" />
          {!collapsed && <span className="text-sm font-semibold text-foreground">OneCheckUp</span>}
        </div>

        <nav className="flex-1 overflow-y-auto overflow-x-hidden pb-3" role="navigation" aria-label="Main navigation">
          <ul className="space-y-1">
            {visibleNavItems.map(({ label, icon: Icon, to, exactMatch }) => {
              const isActive    = isActiveRoute(to, exactMatch);
              const showTooltip = collapsed && activeTooltip === label;
              const isDivider   = label === "Settings" || label === "Debug Logs";

              return (
                  <React.Fragment key={to}>
                    {isDivider && (
                        <li aria-hidden><div className="my-2 mx-3 border-t border-border" /></li>
                    )}
                    <li>
                      <NavLink
                          to={to}
                          aria-label={collapsed ? label : undefined}
                          className={cn(
                              "relative flex items-center rounded-lg text-sm transition-colors",
                              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                              collapsed ? "w-11 h-11 mx-auto justify-center" : "h-10 mx-2 px-3 gap-3",
                              isActive
                                  ? "bg-primary/10 text-primary font-medium"
                                  : "hover:bg-muted hover:text-foreground"
                          )}
                          aria-current={isActive ? "page" : undefined}
                          onClick={() => {
                            // Below lg the expanded rail overlays content — close it after navigating
                            if (!collapsed && window.innerWidth < 1024) toggle();
                          }}
                          onMouseEnter={() => handleItemMouseEnter(label)}
                          onMouseLeave={handleItemMouseLeave}
                      >
                        <Icon className="w-5 h-5 flex-shrink-0 sidebar-icon" />
                        {!collapsed && <span className="whitespace-nowrap">{label}</span>}
                        {showTooltip && (
                            <span className="sidebar-tooltip left-[72px] px-2.5 py-1.5 bg-popover text-popover-foreground text-xs font-medium rounded-md shadow-md border border-border z-[100]">
                              {label}
                            </span>
                        )}
                      </NavLink>
                    </li>
                  </React.Fragment>
              );
            })}
          </ul>
        </nav>

        <button
            type="button"
            onClick={handleToggleClick}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            aria-expanded={!collapsed}
            className={cn(
                "h-12 flex-shrink-0 flex items-center border-t border-border text-muted-foreground",
                "hover:bg-muted hover:text-foreground transition-colors",
                collapsed ? "justify-center" : "justify-end px-4"
            )}
        >
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </button>
      </aside>
  );
}
